import { isChromakeyModeActive } from 'src/utils/env'
import { SCROLL_BAR_WIDTH } from 'src/utils/dom'
import { TRANSCRIPT_FULL_WIDTH_PADDING } from 'src/components/constants'
import { INCONSISTENCY_INDICATOR_SCALE_VAR, getInconsistencyIndicatorScale } from 'src/components/TranscriptViewer/constants'
import { useConfig } from 'src/contexts/ConfigurationContext'
import { useFeatureFlag } from 'src/contexts/FeatureFlagContext'
import { useResponsiveContainer } from 'src/contexts/ResponsiveContainerContext'
import { useTranscriptDimensions } from 'src/hooks/useTranscriptDimensions'

import {
    INCONSISTENCY_INDICATOR_EDGE,
    INCONSISTENCY_INDICATOR_OFFSET,
    PARAGRAPH_CONTAINER_DESKTOP_PADDING,
    PARAGRAPH_CONTAINER_MARGIN,
    PARAGRAPH_CONTAINER_MOBILE_PADDING,
} from './constants'

type GetInconsistencyGutterDto = {
    isMobileLayout: boolean
    containerWidth: number
    paragraphWidth: number
    scale?: number
    // A classic scrollbar narrows the list the paragraph is centred in; an overlay one is 0.
    scrollbarWidth?: number
}

/** The extra left padding a paragraph needs so the icon fits in its margin; none when the margin is wide enough. */
export const getInconsistencyGutter = ({
    isMobileLayout,
    containerWidth,
    paragraphWidth,
    scale = 1,
    scrollbarWidth = SCROLL_BAR_WIDTH,
}: GetInconsistencyGutterDto) => {
    // The paragraph is centred and capped at `paragraphWidth`; the mobile layout also keeps a margin on each side.
    const centredMargin = Math.max(0, (containerWidth - scrollbarWidth - paragraphWidth) / 2)
    const outside = isMobileLayout ? Math.max(PARAGRAPH_CONTAINER_MARGIN, centredMargin) : centredMargin
    const padding = isMobileLayout ? PARAGRAPH_CONTAINER_MOBILE_PADDING : PARAGRAPH_CONTAINER_DESKTOP_PADDING

    return Math.max(0, INCONSISTENCY_INDICATOR_OFFSET * scale + INCONSISTENCY_INDICATOR_EDGE - outside - padding)
}

type GetInconsistencyGutterCssDto = Pick<GetInconsistencyGutterDto, 'isMobileLayout' | 'paragraphWidth' | 'scrollbarWidth'> & {
    isFullWidthMode: boolean
}

/**
 * `getInconsistencyGutter` as a CSS length on the transcript panel's live width (`100cqw`). The panel width in JS
 * lags while the AI panel's split is dragged, which would push the icon past the list's edge until the drag ends.
 */
export const getInconsistencyGutterCss = ({
    isMobileLayout,
    isFullWidthMode,
    paragraphWidth,
    scrollbarWidth = SCROLL_BAR_WIDTH,
}: GetInconsistencyGutterCssDto) => {
    // In full-width mode the paragraph follows the panel too, so its width cannot come from JS either.
    const paragraphWidthCss = isFullWidthMode ? `(100cqw - ${scrollbarWidth + TRANSCRIPT_FULL_WIDTH_PADDING * 2}px)` : `${paragraphWidth}px`
    const centredMargin = `max(0px, (100cqw - ${scrollbarWidth}px - ${paragraphWidthCss}) / 2)`
    const outside = isMobileLayout ? `max(${PARAGRAPH_CONTAINER_MARGIN}px, ${centredMargin})` : centredMargin
    const padding = isMobileLayout ? PARAGRAPH_CONTAINER_MOBILE_PADDING : PARAGRAPH_CONTAINER_DESKTOP_PADDING
    const offset = `${INCONSISTENCY_INDICATOR_OFFSET}px * var(${INCONSISTENCY_INDICATOR_SCALE_VAR}, 1)`

    return `max(0px, ${offset} + ${INCONSISTENCY_INDICATOR_EDGE}px - ${outside} - ${padding}px)`
}

const useIsGutterEnabled = () => useFeatureFlag('insights_inline_inconsistencies') && !isChromakeyModeActive

// Every paragraph reserves the same room while the feature is on, so the text edge stays straight. A JS estimate,
// for sizing paragraphs before they render; the rendered padding is `useInconsistencyGutterCss`.
export const useInconsistencyGutter = (containerWidth: number) => {
    const isEnabled = useIsGutterEnabled()
    const { isMediumContainer } = useResponsiveContainer()
    const { transcriptParagraphWidth } = useTranscriptDimensions(containerWidth)
    const [{ fontSizePercentage }] = useConfig()

    return isEnabled
        ? getInconsistencyGutter({
              isMobileLayout: isMediumContainer,
              containerWidth,
              paragraphWidth: transcriptParagraphWidth,
              scale: getInconsistencyIndicatorScale(fontSizePercentage),
          })
        : 0
}

/** The rendered gutter for the desktop and the tablet/mobile layouts; `0px` while the feature is off. */
export const useInconsistencyGutterCss = (containerWidth: number) => {
    const isEnabled = useIsGutterEnabled()
    const [{ isFullWidthMode }] = useConfig()
    const { transcriptParagraphWidth } = useTranscriptDimensions(containerWidth)

    if (!isEnabled) {
        return { desktop: '0px', mobile: '0px' }
    }

    const dto = { isFullWidthMode, paragraphWidth: transcriptParagraphWidth }

    return {
        desktop: getInconsistencyGutterCss({ ...dto, isMobileLayout: false }),
        mobile: getInconsistencyGutterCss({ ...dto, isMobileLayout: true }),
    }
}
